import { useState, useEffect } from 'react';
import { socket, isSocketAuthenticated } from '../services/Socket';

export const useSocketConnection = () => {
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isAuthenticated, setIsAuthenticated] = useState(isSocketAuthenticated());
  const [connectionError, setConnectionError] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  useEffect(() => {
    const onConnect = () => {
      console.log('Socket connected');
      setIsConnected(true);
      setConnectionError(null);
      setReconnectAttempts(0);
    };

    const onDisconnect = (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);
      setIsAuthenticated(false);
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, try to reconnect
        socket.connect();
      }
    };

    const onConnectError = (error) => {
      console.error('Socket connection error:', error);
      setConnectionError(error.message);
      setIsConnected(false);
      setIsAuthenticated(false);
    };

    const onReconnectAttempt = (attemptNumber) => {
      console.log('Socket reconnection attempt:', attemptNumber);
      setReconnectAttempts(attemptNumber);
    };

    const onReconnect = (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setConnectionError(null);
      setReconnectAttempts(0);
    };

    const onReconnectError = (error) => {
      console.error('Socket reconnection error:', error);
      setConnectionError(error.message);
    };

    const onReconnectFailed = () => {
      console.error('Socket reconnection failed');
      setConnectionError('Reconnection failed');
    };

    const onAuthenticated = (data) => {
      console.log('Socket authenticated:', data);
      setIsAuthenticated(true);
    };

    const onAuthenticationError = (error) => {
      console.error('Socket authentication error:', error);
      setIsAuthenticated(false);
      setConnectionError('Authentication failed: ' + error.message);
    };

    // Set up event listeners
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onConnectError);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect', onReconnect);
    socket.on('reconnect_error', onReconnectError);
    socket.on('reconnect_failed', onReconnectFailed);
    socket.on('authenticated', onAuthenticated);
    socket.on('authentication_error', onAuthenticationError);

    // Check initial connection and authentication state
    setIsConnected(socket.connected);
    setIsAuthenticated(isSocketAuthenticated());

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onConnectError);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect', onReconnect);
      socket.off('reconnect_error', onReconnectError);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('authenticated', onAuthenticated);
      socket.off('authentication_error', onAuthenticationError);
    };
  }, []);

  const forceReconnect = () => {
    if (!socket.connected) {
      socket.connect();
    }
  };

  return {
    isConnected,
    isAuthenticated,
    connectionError,
    reconnectAttempts,
    forceReconnect
  };
};
